import { trackEvent } from './posthog'

// Duplicated from TaskManagerContext to avoid circular import
// (TaskManagerContext imports from lib/analytics, so analytics can't import from it)
type TaskType =
  | 'thumbnail_generation'
  | 'listing_generation'
  | 'design_check'
  | 'field_generation'
  | 'listing_check'

// ── Tier 1 Events ──────────────────────────────────────────────

export function listingCopied(props: { source: 'single' | 'batch'; field?: string }) {
  trackEvent('listing_copied', props)
}

export function listingEdited(props: { field: string; design_id: string }) {
  trackEvent('listing_edited', props)
}

export function taskCompleted(props: {
  type: TaskType
  status: 'completed' | 'failed' | 'cancelled'
  entity_type: string
  entity_id: string
  duration_ms?: number
}) {
  trackEvent('task_completed', props)
}

export function checkoutStarted(props: {
  type: 'subscription' | 'credit_pack'
  plan?: string
  pack_id?: string
}) {
  trackEvent('checkout_started', props)
}

export function checkoutCompleted(props: { type: 'subscription' | 'credit_pack' }) {
  trackEvent('checkout_completed', props)
}

export function fieldGenerated(props: { field: string; design_id: string }) {
  trackEvent('field_generated', props)
}

// ── Tier 2 Events ──────────────────────────────────────────────

export function batchCompleted(props: {
  batch_id: string
  design_count: number
  duration_ms?: number
}) {
  trackEvent('batch_completed', props)
}

export function designDeleted(props: { had_listing: boolean; source: 'single' | 'batch' }) {
  trackEvent('design_deleted', props)
}

export function filterUsed(props: {
  page: 'designs' | 'batches' | 'tasks'
  filter: string
  value: string
}) {
  trackEvent('filter_used', props)
}

// ── Onboarding Events ─────────────────────────────────────────

export function onboardingEmptyStateViewed(props: { page: string; credits_remaining: number }) {
  trackEvent('onboarding_empty_state_viewed', props)
}

export function onboardingEmptyStateCTAClicked(props: { page: string }) {
  trackEvent('onboarding_empty_state_cta_clicked', props)
}

// ── Upload Funnel Events ──────────────────────────────────────

export function uploadStarted(props: { source: string }) {
  trackEvent('upload_started', props)
}

export function uploadStepViewed(props: { step: number; step_name: string }) {
  trackEvent('upload_step_viewed', props)
}

export function uploadAbandoned(props: { last_step: number; files_selected: number }) {
  trackEvent('upload_abandoned', props, { sendInstantly: true })
}

// ── Engagement Events ─────────────────────────────────────────

export function dashboardViewed(props: { is_new_user: boolean }) {
  trackEvent('dashboard_viewed', props)
}

export function designDetailViewed(props: { design_id: string; has_listing: boolean }) {
  trackEvent('design_detail_viewed', props)
}

export function customInstructionsUsed(props: { design_id: string; instruction_length: number }) {
  trackEvent('custom_instructions_used', props)
}

// ── Existing events (typed wrappers for migration) ─────────────

export function designUploaded(props: { file_count: number; source: 'single' }) {
  trackEvent('design_uploaded', props, { sendInstantly: true })
}

export function batchCreated(props: { design_count: number }) {
  trackEvent('batch_created', props, { sendInstantly: true })
}

export function batchGenerated(props: { design_count: number }) {
  trackEvent('batch_generated', props)
}

export function listingGenerated(props: { design_id: string }) {
  trackEvent('listing_generated', props)
}

export function listingExported(props: {
  count: number
  format: string
  source: 'single' | 'batch'
}) {
  trackEvent('listing_exported', props)
}

export function creditsDepleted(props: { trigger: string }) {
  trackEvent('credits_depleted', props)
}
